/**
 * @deprecated Superseded by `useDocumentCollab`'s awareness output.
 * `MEDSTUDENT_LIVE_COLLAB` (see src/config/collab.js) is now hardcoded ON, so
 * this hook only runs if someone flips that constant back to `false` — it
 * remains as the presence fallback for that path. Safe to delete along with
 * the flag's OFF branch.
 */
import { useEffect, useState } from 'react';
import { getSupabase } from '../../components/supabaseClient';

function computeInitials({ name, email }) {
  if (name && typeof name === 'string') {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
  }
  if (email && typeof email === 'string' && email.length > 0) return email[0].toUpperCase();
  return '?';
}

// Subscribes to a Supabase Realtime presence channel keyed by `documentPath`.
// Every connected viewer announces { userId, name, email, initials }, and we
// return the deduped list of all current viewers (most-recently-joined first).
//
// `user` must be the result of useUserInfo() — when userId is null we skip
// subscribing because anonymous viewers shouldn't pollute presence.
export function useDocumentPresence(documentPath, user) {
  const [viewers, setViewers] = useState([]);

  useEffect(() => {
    if (!documentPath || !user?.userId) {
      setViewers([]);
      return;
    }
    let cancelled = false;
    let channel = null;

    (async () => {
      const sb = await getSupabase();
      if (cancelled) return;
      const channelName = `medstudent-doc:${documentPath}`;
      channel = sb.channel(channelName, {
        config: { presence: { key: user.userId } },
      });
      channel.on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        // state shape: { [key]: [{ userId, name, ... }, ...] }
        const flat = [];
        const seen = new Set();
        for (const key of Object.keys(state)) {
          for (const entry of state[key]) {
            if (entry?.userId && !seen.has(entry.userId)) {
              seen.add(entry.userId);
              flat.push(entry);
            }
          }
        }
        // Stable order: current user first, others by name.
        flat.sort((a, b) => {
          if (a.userId === user.userId) return -1;
          if (b.userId === user.userId) return 1;
          return (a.name || a.email || '').localeCompare(b.name || b.email || '');
        });
        if (!cancelled) setViewers(flat);
      });
      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            userId: user.userId,
            name: user.name,
            email: user.email,
            initials: user.initials,
          });
        }
      });
    })();

    return () => {
      cancelled = true;
      if (channel) {
        try { channel.untrack(); } catch {}
        try { getSupabase().then(sb => sb.removeChannel(channel)); } catch {}
      }
    };
  }, [documentPath, user?.userId, user?.name, user?.email, user?.initials]);

  const result = viewers.map((v) => ({
    id: v.userId,
    name: v.name || v.email || 'Viewer',
    initials: v.initials || computeInitials({ name: v.name, email: v.email }),
  }));

  // The Realtime channel's first 'sync' fires AFTER subscribe() but BEFORE
  // our local track() resolves — during that window presenceState() reports
  // 0 viewers and `viewers` is set to []. IconRail then shows its fallback
  // purple "You" avatar; once track resolves, the next sync repopulates with
  // self and IconRail flips to the cyan/blue presence avatar. The user
  // perceives this as a purple flash on patient switch. Self is by definition
  // always present in their own session, so prepend if missing.
  if (user?.userId && !result.some((v) => v.id === user.userId)) {
    result.unshift({
      id: user.userId,
      name: user.name || user.email || 'You',
      initials: user.initials || computeInitials({ name: user.name, email: user.email }),
    });
  }

  return result;
}
